// Pure helpers for the contributors modal: the four fixed role groups the title page shows,
// each holding people with the fields the page renders. `origIndex` is the person's position
// in the book's existing <authorgroup>; the server reuses that <author> element so children the
// modal does not edit (email, blurb) survive. New people have origIndex null.

export const PERSON_FIELDS = [
  'honorific',
  'firstname',
  'othername',
  'surname',
  'orgname',
]

export const PERSON_FIELD_LABELS = {
  honorific: 'Title',
  firstname: 'First name',
  othername: 'Middle name',
  surname: 'Last name',
  orgname: 'Organization',
}

export const emptyPerson = () => ({
  origIndex: null,
  honorific: '',
  firstname: '',
  othername: '',
  surname: '',
  orgname: '',
})

const normalizePerson = person => {
  const row = emptyPerson()
  PERSON_FIELDS.forEach(field => {
    row[field] = person && person[field] != null ? String(person[field]) : ''
  })
  row.origIndex =
    person && Number.isInteger(person.origIndex) && person.origIndex >= 0
      ? person.origIndex
      : null
  return row
}

// server payload ({groups: [{key, role, people}]}) -> editable state
export const groupsFromResponse = data =>
  ((data && data.groups) || []).map(group => ({
    key: group.key,
    role: group.role,
    people: (group.people || []).map(normalizePerson),
  }))

const updateGroup = (groups, key, fn) =>
  groups.map(group =>
    group.key === key ? { ...group, people: fn(group.people) } : group
  )

export const updatePerson = (groups, key, index, field, value) =>
  updateGroup(groups, key, people =>
    people.map((person, i) =>
      i === index ? { ...person, [field]: value } : person
    )
  )

export const addPerson = (groups, key) =>
  updateGroup(groups, key, people => [...people, emptyPerson()])

export const removePerson = (groups, key, index) =>
  updateGroup(groups, key, people => people.filter((_, i) => i !== index))

// delta is -1 (up) or +1 (down); out-of-range moves are no-ops
export const movePerson = (groups, key, index, delta) =>
  updateGroup(groups, key, people => {
    const target = index + delta
    if (index < 0 || index >= people.length) return people
    if (target < 0 || target >= people.length) return people
    const next = [...people]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    return next
  })

export const isPersonBlank = person =>
  PERSON_FIELDS.every(field => !(person[field] || '').trim())

// Blank rows (an "Add person" the author never filled in) are dropped rather than rejected.
export const buildPayload = groups => ({
  groups: groups.map(group => ({
    key: group.key,
    people: group.people
      .filter(person => !isPersonBlank(person))
      .map(person => {
        const row = { origIndex: person.origIndex }
        PERSON_FIELDS.forEach(field => {
          row[field] = (person[field] || '').trim()
        })
        return row
      }),
  })),
})

// mirrors the server rule: a person needs a first name, last name, or organization
export const validatePayload = payload => {
  for (const group of payload.groups) {
    for (const person of group.people) {
      if (!(person.firstname || person.surname || person.orgname)) {
        return `Each person under ${group.key} needs a first name, last name, or organization.`
      }
    }
  }
  return null
}

export const errorMessage = (err, fallback) =>
  (err && err.response && err.response.data && err.response.data.error) ||
  (err && err.message) ||
  fallback
